import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, User } from 'lucide-react';
import { contactDisplayName, type Contact } from '../../../types';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';

type SiteContactPickerProps = {
  value: Contact | null;
  disabled?: boolean;
  onSelect: (contact: Contact | null) => void | Promise<void>;
  /** Override portal z-index (default: above modals). */
  menuZIndex?: number;
};

type MenuPos = {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
};

const VIEWPORT_MARGIN = 8;
const PANEL_GAP = 4;
const MIN_PANEL_HEIGHT = 160;
const PREFERRED_MAX_HEIGHT = 320;

/**
 * Button+Panel picker for optional Baustellenkontakt (search existing or create Privatkunde inline).
 */
export const SiteContactPicker: React.FC<SiteContactPickerProps> = ({
  value,
  disabled,
  onSelect,
  menuZIndex = UI_Z_INDEX.portalDropdownInModal,
}) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [results, setResults] = React.useState<Contact[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);
  const [menuPos, setMenuPos] = React.useState<MenuPos | null>(null);

  const wrapRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  const display = value
    ? `${contactDisplayName(value) || 'Kontakt'}${value.phone ? ` · ${value.phone}` : ''}`
    : '— Kein Baustellenkontakt —';

  const resetCreateForm = () => {
    setFirstName('');
    setLastName('');
    setPhone('');
    setEmail('');
    setCreateError(null);
    setSaving(false);
  };

  const startCreate = () => {
    setOpen(false);
    setSearch('');
    resetCreateForm();
    setCreating(true);
  };

  const cancelCreate = () => {
    setCreating(false);
    resetCreateForm();
  };

  const saveCreate = async () => {
    const ln = lastName.trim();
    if (!ln) {
      setCreateError('Nachname ist erforderlich.');
      return;
    }
    setSaving(true);
    setCreateError(null);
    try {
      const res = await fetch('/api/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_kind: 'Privatkunde',
          first_name: firstName.trim() || null,
          last_name: ln,
          phone: phone.trim() || null,
          email: email.trim() || null,
        }),
      });
      const payload = (await res.json().catch(() => ({}))) as Contact & { error?: string };
      if (!res.ok) throw new Error(payload?.error || 'Kontakt konnte nicht erstellt werden.');
      await onSelect(payload);
      setCreating(false);
      resetCreateForm();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Kontakt konnte nicht erstellt werden.');
    } finally {
      setSaving(false);
    }
  };

  const updateMenuPosition = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN - PANEL_GAP;
    const spaceAbove = rect.top - VIEWPORT_MARGIN - PANEL_GAP;
    const opensAbove = spaceBelow < Math.min(PREFERRED_MAX_HEIGHT, 160) && spaceAbove > spaceBelow;
    const available = opensAbove ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(MIN_PANEL_HEIGHT, Math.min(PREFERRED_MAX_HEIGHT, Math.max(available, MIN_PANEL_HEIGHT)));

    const width = Math.max(rect.width, 280);
    let left = rect.left;
    if (left + width > window.innerWidth - VIEWPORT_MARGIN) {
      left = Math.max(VIEWPORT_MARGIN, window.innerWidth - VIEWPORT_MARGIN - width);
    }
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;

    if (opensAbove) {
      setMenuPos({
        bottom: window.innerHeight - rect.top + PANEL_GAP,
        left,
        width,
        maxHeight,
      });
      return;
    }
    setMenuPos({
      top: rect.bottom + PANEL_GAP,
      left,
      width,
      maxHeight,
    });
  }, []);

  React.useLayoutEffect(() => {
    if (!open || creating) {
      setMenuPos(null);
      return;
    }
    updateMenuPosition();
    const openedAt = Date.now();
    const onScroll = (e: Event) => {
      if (Date.now() - openedAt < 150) return;
      const t = e.target as Node | null;
      if (!t) return;
      if (listRef.current && (listRef.current === t || listRef.current.contains(t))) return;
      updateMenuPosition();
    };
    window.addEventListener('resize', updateMenuPosition);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open, creating, updateMenuPosition]);

  React.useEffect(() => {
    if (!open || creating) return;
    const onOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
      setSearch('');
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    };
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onOutside);
    }, 0);
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, creating]);

  React.useEffect(() => {
    if (!open || creating) return;
    let cancelled = false;
    const q = search.trim();
    const t = window.setTimeout(() => {
      void (async () => {
        setLoading(true);
        try {
          const url = q ? `/api/contacts?q=${encodeURIComponent(q)}` : '/api/contacts';
          const res = await fetch(url);
          const data = (await res.json().catch(() => [])) as Contact[];
          if (!cancelled) setResults(Array.isArray(data) ? data.slice(0, 50) : []);
        } catch {
          if (!cancelled) setResults([]);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, q ? 200 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [open, creating, search]);

  const pick = async (contact: Contact | null) => {
    await onSelect(contact);
    setOpen(false);
    setSearch('');
  };

  if (creating) {
    const fieldLabelClass = 'text-[10px] font-bold text-slate-400 uppercase tracking-wide';
    const fieldInputClass =
      'mt-0.5 w-full rounded border border-app-border px-2 py-1.5 text-sm font-normal normal-case text-app-text outline-none focus:ring-1 focus:ring-pipedrive-blue disabled:opacity-60';
    const btnClass =
      'inline-flex h-8 items-center justify-center rounded border px-3 text-xs font-medium whitespace-nowrap disabled:opacity-50';
    return (
      <div className="space-y-2 rounded-lg border border-app-border bg-app-surface p-3">
        <p className="text-[11px] font-medium text-app-text-muted normal-case">Neuer Baustellenkontakt</p>
        <div>
          <label className={fieldLabelClass} htmlFor="site-contact-create-first-name">
            Vorname
          </label>
          <input
            id="site-contact-create-first-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={disabled || saving}
            className={fieldInputClass}
          />
        </div>
        <div>
          <label className={fieldLabelClass} htmlFor="site-contact-create-last-name">
            Nachname <span className="text-red-500">*</span>
          </label>
          <input
            id="site-contact-create-last-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={disabled || saving}
            className={fieldInputClass}
          />
        </div>
        <div>
          <label className={fieldLabelClass} htmlFor="site-contact-create-phone">
            Telefon
          </label>
          <input
            id="site-contact-create-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={disabled || saving}
            className={fieldInputClass}
          />
        </div>
        <div>
          <label className={fieldLabelClass} htmlFor="site-contact-create-email">
            E-Mail
          </label>
          <input
            id="site-contact-create-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={disabled || saving}
            className={fieldInputClass}
          />
        </div>
        {createError && <p className="text-[11px] font-normal normal-case text-red-600 leading-snug">{createError}</p>}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={cancelCreate}
            disabled={saving}
            className={`${btnClass} border-app-border bg-app-surface text-app-text-muted hover:bg-app-hover`}
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => void saveCreate()}
            disabled={disabled || saving}
            className={`${btnClass} border-transparent bg-pipedrive-blue text-white hover:opacity-90`}
          >
            {saving ? 'Speichert…' : 'Erstellen'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-w-0" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (disabled) return;
          setOpen((o) => {
            if (o) setSearch('');
            return !o;
          });
        }}
        className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-app-border bg-app-surface px-2.5 text-left text-sm outline-none focus:border-pipedrive-blue focus:ring-1 focus:ring-pipedrive-blue disabled:opacity-60"
      >
        <span className={`truncate flex items-center gap-1.5 min-w-0 ${value ? 'text-app-text' : 'text-app-text-subtle'}`}>
          <User className="w-3.5 h-3.5 shrink-0 text-slate-400" />
          <span className="truncate">{display}</span>
        </span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 text-app-text-subtle transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open &&
        menuPos &&
        createPortal(
          <div
            ref={panelRef}
            role="listbox"
            data-site-contact-picker-list
            style={{
              position: 'fixed',
              top: menuPos.top,
              bottom: menuPos.bottom,
              left: menuPos.left,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
              zIndex: menuZIndex,
            }}
            className="bg-app-surface border border-app-border rounded-lg shadow-lg overflow-hidden flex flex-col min-h-0 overscroll-y-contain"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
          >
            <div className="p-2 border-b border-app-border shrink-0">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Kontakt suchen…"
                  autoFocus
                  className="w-full pl-9 pr-2 py-2 text-[13px] border border-app-border rounded-md focus:ring-1 focus:ring-pipedrive-blue outline-none"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => void pick(null)}
              className={`w-full shrink-0 text-left px-2.5 py-2.5 text-[13px] font-medium border-b border-app-border transition-colors ${
                value == null ? 'bg-app-surface-muted text-app-text' : 'text-app-text-muted hover:bg-app-hover'
              }`}
            >
              — Kein Baustellenkontakt —
            </button>
            <button
              type="button"
              onClick={startCreate}
              className="w-full shrink-0 text-left px-2.5 py-2.5 text-[13px] font-medium text-pipedrive-blue hover:bg-app-hover border-b border-app-border"
            >
              + Neuen Kontakt erstellen
            </button>
            <div ref={listRef} className="overflow-y-auto flex-1 min-h-0 touch-pan-y">
              {loading ? (
                <p className="px-2.5 py-2.5 text-[13px] text-app-text-subtle">Lädt…</p>
              ) : results.length === 0 ? (
                <p className="px-2.5 py-2.5 text-[13px] text-app-text-subtle">Keine Treffer.</p>
              ) : (
                results.map((c) => {
                  const active = value != null && String(c.id) === String(value.id);
                  const label = contactDisplayName(c) || c.email || `Kontakt #${c.id}`;
                  const sub = [c.phone, c.company].filter((x) => String(x ?? '').trim()).join(' · ');
                  return (
                    <button
                      key={String(c.id)}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => void pick(c)}
                      className={`w-full text-left px-2.5 py-2.5 text-[13px] font-medium transition-colors ${
                        active ? 'bg-pipedrive-blue text-white' : 'text-app-text hover:bg-app-hover'
                      }`}
                    >
                      <span className="block truncate">{label}</span>
                      {sub ? (
                        <span className={`block truncate text-[11px] ${active ? 'text-white/80' : 'text-app-text-subtle'}`}>
                          {sub}
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
